/**
 * CabochonUploader - the octagons and the corner squares of a cabochon floor,
 * in two labelled uploaders. The variant keeps them as one plank list (octagons
 * then squares, see utils/cabochonImages), which this splits and joins.
 */

import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CircleAlert } from 'lucide-react';
import { PlankUploader } from './PlankUploader';
import { isCabochonBalanced, joinCabochonImages, splitCabochonImages } from '../../../utils/cabochonImages';

interface CabochonUploaderProps {
  plankGroupId: string;
  files: File[];
  onChange: (files: File[]) => void;
}

export const CabochonUploader: React.FC<CabochonUploaderProps> = ({ plankGroupId, files, onChange }) => {
  const { t } = useTranslation();
  const [parts, setParts] = useState(() => (files.length > 0 ? splitCabochonImages(files) : { octagons: [], squares: [] }));
  // The list last sent up: when the parent hands back anything else (a reload,
  // Build from photos), split it afresh.
  const emittedRef = useRef<File[]>(files);

  useEffect(() => {
    if (files === emittedRef.current) return;
    emittedRef.current = files;
    setParts(files.length > 0 ? splitCabochonImages(files) : { octagons: [], squares: [] });
  }, [files]);

  const update = (octagons: File[], squares: File[]) => {
    setParts({ octagons, squares });
    const joined = joinCabochonImages(octagons, squares);
    emittedRef.current = joined;
    onChange(joined);
  };

  const { octagons, squares } = parts;
  const unbalanced = (octagons.length > 0 || squares.length > 0) && !isCabochonBalanced(octagons, squares);

  return (
    <div className="flex flex-col gap-4" onClick={(e) => e.stopPropagation()}>
      <div className="flex flex-col gap-2">
        <p className="text-xs font-medium text-muted-foreground">
          {t('PAGES.NEW_PRODUCT.V3.SURFACE.CABOCHON_OCTAGONS')}
        </p>
        <PlankUploader
          plankGroupId={`${plankGroupId}-octagons`}
          files={octagons}
          onChange={(next) => update(next, squares)}
          mode="multiple"
          title={t('PAGES.NEW_PRODUCT.V3.SURFACE.CABOCHON_UPLOAD_OCTAGONS')}
        />
      </div>
      <div className="flex flex-col gap-2">
        <p className="text-xs font-medium text-muted-foreground">
          {t('PAGES.NEW_PRODUCT.V3.SURFACE.CABOCHON_SQUARES')}
        </p>
        <PlankUploader
          plankGroupId={`${plankGroupId}-squares`}
          files={squares}
          onChange={(next) => update(octagons, next)}
          mode="multiple"
          title={t('PAGES.NEW_PRODUCT.V3.SURFACE.CABOCHON_UPLOAD_SQUARES')}
        />
      </div>
      {unbalanced ? (
        <div className="flex items-center gap-1.5 border border-warning/20 bg-warning/10 px-3 py-1.5 text-xs text-warning">
          <CircleAlert className="size-4 shrink-0" />
          {t('PAGES.NEW_PRODUCT.V3.SURFACE.CABOCHON_UNBALANCED', { octagons: octagons.length, squares: squares.length })}
        </div>
      ) : (
        <p className="text-xs text-muted-foreground">{t('PAGES.NEW_PRODUCT.V3.SURFACE.CABOCHON_SIZE_HINT')}</p>
      )}
    </div>
  );
};
